/**
 * Pose Alignment Service (MediaPipe BlazePose Lite)
 * ==================================================
 *
 * Runs entirely client-side. Model (~3 MB) is fetched once from Google CDN
 * and cached by the browser. No server cost, no user data leaves the device.
 *
 * Public API:
 *   - getPoseLandmarker()        → lazy-loaded singleton
 *   - detectPose(source, ts?)    → returns normalized 33-landmark array or null
 *   - poseSimilarity(a, b)       → 0..1 score comparing two pose snapshots
 */

import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';

let _landmarker = null;
let _loadPromise = null;

const POSE_MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task';
const WASM_PATH = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm';

export async function getPoseLandmarker() {
  if (_landmarker) return _landmarker;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    const vision = await FilesetResolver.forVisionTasks(WASM_PATH);
    _landmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    return _landmarker;
  })();

  return _loadPromise;
}

/**
 * Detect the main pose in a video / image element.
 * Returns an array of 33 normalized landmarks {x, y, z, visibility} or null.
 */
export function detectPose(source, timestampMs = performance.now()) {
  if (!_landmarker || !source) return null;
  try {
    let result;
    if (source instanceof HTMLVideoElement) {
      result = _landmarker.detectForVideo(source, timestampMs);
    } else if (source instanceof HTMLImageElement) {
      // Switch mode momentarily for static images
      // We keep the running mode VIDEO and reuse the video API by
      // wrapping the image in a canvas-sized HTMLVideoElement is not trivial,
      // so instead we expose a dedicated static-image detector below.
      return null;
    } else {
      return null;
    }
    return result?.landmarks?.[0] || null;
  } catch (err) {
    console.warn('[PoseService] detectPose error:', err);
    return null;
  }
}

/**
 * Detect pose in a static image. Creates a short-lived IMAGE-mode landmarker.
 * Use this to pre-compute the reference pose (ghost image) once per session.
 */
export async function detectPoseInImage(imageEl) {
  if (!imageEl) return null;
  try {
    const vision = await FilesetResolver.forVisionTasks(WASM_PATH);
    const imgLandmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: 'GPU' },
      runningMode: 'IMAGE',
      numPoses: 1,
    });
    const result = imgLandmarker.detect(imageEl);
    imgLandmarker.close();
    return result?.landmarks?.[0] || null;
  } catch (err) {
    console.warn('[PoseService] detectPoseInImage error:', err);
    return null;
  }
}

/**
 * Compute a 0..1 similarity score between two 33-landmark poses.
 *
 * Method:
 *  1. Normalize each pose by translating to hip-center and scaling by torso length
 *     (so poses are invariant to camera distance and position in frame).
 *  2. Compute mean euclidean distance over a subset of robust upper-body
 *     + full-body key points (excluding face landmarks which are noisy).
 *  3. Map to 0..1 where 0 = identical, 1 = very different, then return 1 - dist.
 */
const KEY_LANDMARKS = [
  11, 12, 13, 14, 15, 16,   // shoulders, elbows, wrists
  23, 24, 25, 26, 27, 28,   // hips, knees, ankles
];

export function poseSimilarity(a, b) {
  if (!a || !b || a.length < 33 || b.length < 33) return 0;

  const normalize = (lm) => {
    // Hip center (landmarks 23 = left hip, 24 = right hip)
    const hipCx = (lm[23].x + lm[24].x) / 2;
    const hipCy = (lm[23].y + lm[24].y) / 2;
    // Shoulder center (11 = left shoulder, 12 = right shoulder)
    const shoulderCx = (lm[11].x + lm[12].x) / 2;
    const shoulderCy = (lm[11].y + lm[12].y) / 2;
    // Torso length for scale normalization
    const torso = Math.hypot(shoulderCx - hipCx, shoulderCy - hipCy) || 1;
    return { cx: hipCx, cy: hipCy, torso };
  };

  const nA = normalize(a);
  const nB = normalize(b);

  let totalDist = 0;
  let count = 0;
  for (const idx of KEY_LANDMARKS) {
    if (!a[idx] || !b[idx]) continue;
    // Confidence gating — low-visibility points shouldn't penalize the score
    if ((a[idx].visibility ?? 1) < 0.5 || (b[idx].visibility ?? 1) < 0.5) continue;
    const ax = (a[idx].x - nA.cx) / nA.torso;
    const ay = (a[idx].y - nA.cy) / nA.torso;
    const bx = (b[idx].x - nB.cx) / nB.torso;
    const by = (b[idx].y - nB.cy) / nB.torso;
    totalDist += Math.hypot(ax - bx, ay - by);
    count += 1;
  }
  if (count < 4) return 0; // not enough visible landmarks to compare confidently

  const avgDist = totalDist / count;
  // Calibrated mapping: avg distance > 0.4 → 0%, < 0.05 → 100%
  const score = Math.max(0, Math.min(1, 1 - (avgDist - 0.05) / 0.35));
  return score;
}


/**
 * Clinical posture analysis from BlazePose landmarks.
 * Returns a compact object with measurable asymmetries (all in normalized
 * image units, 0..1) + severity classification.
 *
 * Landmarks used (BlazePose indexing):
 *   0  = nose
 *   7, 8  = left/right ear
 *   11, 12 = left/right shoulder
 *   23, 24 = left/right hip
 *   25, 26 = left/right knee
 *   27, 28 = left/right ankle
 */
export function analyzePosture(landmarks) {
  if (!landmarks || landmarks.length < 33) {
    return { severity: "unavailable", asymmetries: [] };
  }

  const L = landmarks;
  const vis = (i) => (L[i]?.visibility ?? 0);

  // 1) Shoulder height difference (left y vs right y)
  const shoulderDiff = vis(11) > 0.5 && vis(12) > 0.5
    ? Math.abs(L[11].y - L[12].y)
    : null;

  // 2) Pelvis tilt (hip y delta)
  const pelvisTilt = vis(23) > 0.5 && vis(24) > 0.5
    ? Math.abs(L[23].y - L[24].y)
    : null;

  // 3) Head tilt — ear y delta (or fallback to nose x offset vs shoulder midline)
  let headTilt = null;
  if (vis(7) > 0.5 && vis(8) > 0.5) {
    headTilt = Math.abs(L[7].y - L[8].y);
  } else if (vis(0) > 0.5 && vis(11) > 0.5 && vis(12) > 0.5) {
    const shoulderMidX = (L[11].x + L[12].x) / 2;
    headTilt = Math.abs(L[0].x - shoulderMidX);
  }

  // 4) Knee valgus/varus — x distance between knees vs hips (normalized by hip width)
  let hipKneeValgus = null;
  if (vis(23) > 0.5 && vis(24) > 0.5 && vis(25) > 0.5 && vis(26) > 0.5) {
    const hipWidth = Math.abs(L[23].x - L[24].x) || 1e-6;
    const kneeWidth = Math.abs(L[25].x - L[26].x);
    // Ratio < 1 → valgus (knees closer than hips), > 1 → varus
    hipKneeValgus = kneeWidth / hipWidth;
  }

  // Severity rules (empirical thresholds, calibrated for selfie distance ~1.5m)
  const asymmetries = [];
  let severity = "normal";

  if (shoulderDiff !== null && shoulderDiff > 0.025) {
    asymmetries.push("shoulder_imbalance");
    if (shoulderDiff > 0.05) severity = "moderate";
  }
  if (pelvisTilt !== null && pelvisTilt > 0.02) {
    asymmetries.push("pelvic_tilt");
    if (pelvisTilt > 0.04) severity = "moderate";
  }
  if (headTilt !== null && headTilt > 0.03) {
    asymmetries.push("head_tilt");
  }
  if (hipKneeValgus !== null) {
    if (hipKneeValgus < 0.7) asymmetries.push("knee_valgus");
    else if (hipKneeValgus > 1.4) asymmetries.push("knee_varus");
  }

  if (asymmetries.length >= 3 && severity !== "severe") severity = "moderate";
  if (asymmetries.length >= 3 && (shoulderDiff > 0.05 || pelvisTilt > 0.04)) severity = "severe";

  return {
    shoulder_height_diff: shoulderDiff,
    pelvis_tilt:          pelvisTilt,
    head_tilt:            headTilt,
    hip_knee_valgus:      hipKneeValgus,
    asymmetries,
    severity,
  };
}
